import type { WidgetConfig, AvailabilityResponse } from '../types';
import { parseConfig } from '../config';
import { injectCSS, getStyles } from '../styles';
import { getLayoutRenderer } from '../components/layouts';
import { fetchAvailability, getZipFromGeolocation, AvailabilityApiError } from '../api';
import { Analytics } from '../analytics';
import { getWeekDates, getWeekOffsetForDate } from '../utils';
import { shouldOpenModalFromQuery, getRedirectAttribution } from '../utils/url';

const styles = getStyles();

/**
 * Main widget class
 */
export class ProAvailabilityWidget {
  private container: HTMLElement;
  private config: WidgetConfig;
  private layout: any;
  private analytics: Analytics;
  private weekOffset: number = 0;
  private isLoading: boolean = false;
  private lastQueryKey: string | null = null; // Use null instead of empty string to allow first call
  private appliedInitialHashWeek: boolean = false;
  // `view` is sent once per page load. Week/filter reloads are tracked separately
  // (week_nav, filter) and don't re-send it.
  private viewSent: boolean = false;

  constructor(container: HTMLElement) {
    this.container = container;
    this.config = parseConfig(container);

    // A partner's "Show more" redirect carries their instance/utm_content in the hash.
    // Adopt it before anything reads the config, so events and profile links fired on
    // this page stay attributed to the partner rather than to this embed.
    const attribution = getRedirectAttribution();
    if (attribution.instance) this.config.instance = attribution.instance;
    if (attribution.utmContent) this.config.utmContent = attribution.utmContent;

    this.analytics = new Analytics(this.config.instance, this.config.utmContent);

    // Apply redirect hash (week + filters) before first render so calendar shows correct week
    this.applyInitialHashIfPresent();

    // Inject CSS
    injectCSS(styles);

    // Initialize layout
    this.layout = getLayoutRenderer(this.config.layout, this.container, this.config);

    // Render initial empty state so DOM elements exist
    this.layout.render({}, this.weekOffset);
    this.layout.setLoading(true);

    // Auto-prefill zip if not provided, then load availability
    this.initializeWithZip().catch((error) => {
      console.error('Failed to initialize widget:', error);
      // Still load availability even if zip detection fails
      this.loadAvailability();
    }).catch((error) => {
      // If loadAvailability also fails, log it
      console.error('Failed to load availability after initialization error:', error);
    });

    // Listen for internal events
    this.setupEventListeners();
  }

  /**
   * Initializes widget with zip using browser geolocation (user must allow).
   * Skips geolocation when we already applied redirect hash (e.g. no zip in URL = show all pros).
   */
  private async initializeWithZip(): Promise<void> {
    if (this.appliedInitialHashWeek) {
      this.loadAvailability();
      return;
    }
    if (!this.config.zipCode) {
      try {
        const zip = await getZipFromGeolocation();
        // Re-check after await: user may have typed a zip while geolocation was in flight.
        // Don't overwrite a user-supplied zip with the geolocation result.
        if (zip && !this.config.zipCode) {
          this.config.zipCode = zip;
          requestAnimationFrame(() => {
            const zipInput = this.container.querySelector('#ondiem-zip-input') as HTMLInputElement;
            if (zipInput && !zipInput.value) zipInput.value = zip;
          });
        }
      } catch {
        // Continue without zip when geolocation is denied or unavailable
      }
    }
    // Only load if no call has been triggered yet. If lastQueryKey is set, the user already
    // triggered a filter-change load while geolocation was resolving — don't fire again.
    if (this.lastQueryKey === null) {
      this.loadAvailability();
    }
  }

  /**
   * Sets up event listeners for widget interactions
   */
  private setupEventListeners(): void {
    // Filter changes
    this.container.addEventListener('ondiem:filter-change', ((e: CustomEvent) => {
      const { zip, position } = e.detail;
      this.config.zipCode = zip || null;
      this.config.position = position || null;
      this.analytics.event('filter', {
        position_type: position || '(any)',
        zip: zip || '(none)',
      });
      this.loadAvailability();
    }) as EventListener);

    // Week navigation
    this.container.addEventListener('ondiem:week-change', ((e: CustomEvent) => {
      const { direction, offset } = e.detail;
      this.weekOffset = offset;
      this.analytics.event('week_nav', {
        direction,
        week_offset: offset,
      });
      this.loadAvailability();
    }) as EventListener);
  }

  /**
   * If the page was opened from a redirect with openAvailability=1 and date in the hash,
   * set weekOffset and zip/position from the hash. When zip is not in the hash (e.g. 3rd
   * party had empty zip), we set zip to null so the first load shows all pros, not geolocation zip.
   */
  private applyInitialHashIfPresent(): void {
    if (this.appliedInitialHashWeek) return;
    const params = shouldOpenModalFromQuery();
    if (!params.open || !params.date) return;
    this.weekOffset = getWeekOffsetForDate(params.date);
    // Apply zip/position from redirect: if param is absent, use null (show all pros / any position)
    this.config.zipCode = params.zip !== undefined ? (params.zip || null) : null;
    this.config.position = params.position !== undefined ? (params.position || null) : null;
    this.appliedInitialHashWeek = true;
  }

  /**
   * Loads availability data and renders
   */
  private async loadAvailability(): Promise<void> {
    this.applyInitialHashIfPresent();
    const queryKey = this.getQueryKey();
    if (this.isLoading) return;
    if (this.lastQueryKey !== null && queryKey === this.lastQueryKey) return;

    this.isLoading = true;
    this.lastQueryKey = queryKey;
    this.layout.setLoading(true);

    try {
      let { start, end } = getWeekDates(this.weekOffset);
      
      // Ensure date range is valid (max 35 days per API)
      // Note: Weekly calendar always uses 7 days, so this check is defensive
      const startDate = new Date(start);
      const endDate = new Date(end);
      const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff > 35) {
        const limitedEnd = new Date(startDate);
        limitedEnd.setDate(limitedEnd.getDate() + 34);
        end = limitedEnd.toISOString().split('T')[0];
      }

      const response = await fetchAvailability({
        start_date: start,
        end_date: end,
        zip_code: this.config.zipCode || null,
        position: this.config.position || null,
      });

      // API response format: { [date: string]: { available_professional_summaries: ... } }
      // Normalize response to ensure consistent structure
      const availability: AvailabilityResponse = {};
      Object.entries(response || {}).forEach(([date, data]) => {
        availability[date] = {
          available_professional_summaries: data.available_professional_summaries || [],
          available_professionals: data.available_professionals,
        };
      });

      // Render layout
      this.layout.render(availability, this.weekOffset);
      this.layout.setLoading(false);

      // Emit analytics view event
      const totals = Object.values(availability).reduce(
        (sum, d) => sum + (d.available_professional_summaries?.length || d.available_professionals || 0),
        0
      );

      if (!this.viewSent) {
        this.viewSent = true;
        this.analytics.view({
          kind: 'week',
          week_start: start,
          week_end: end,
          position_type: this.config.position || '(any)',
          zip: this.config.zipCode || '(none)',
          results_count: totals,
        });
      }
    } catch (error) {
      this.layout.setLoading(false);
      const isZipError = error instanceof AvailabilityApiError && error.errorCode === 'zip_code_not_found';
      if (error instanceof AvailabilityApiError && error.status !== 429) {
        console.error('API error:', error.message);
      } else if (!(error instanceof AvailabilityApiError)) {
        console.error('Unexpected error loading availability:', error);
      }
      this.layout.render({}, this.weekOffset);
      if (isZipError) {
        this.layout.setZipError(true);
      }
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Gets a unique query key for caching
   */
  private getQueryKey(): string {
    return `${this.weekOffset}|${this.config.position || ''}|${this.config.zipCode || ''}`;
  }
}

/**
 * Initializes all widgets on the page.
 * Guards against double-initialization: if initWidgets() is called more than once
 * (e.g. from Vite HMR, a partner page calling window.OnDiemProAvailability.init(),
 * or any other path), containers that already have a widget are skipped.
 */
export function initWidgets(): void {
  const containers = document.querySelectorAll<HTMLElement>('[data-ondiem-pro-availability]');
  containers.forEach((container) => {
    if (container.dataset.ondiemInitialized === '1') return;
    container.dataset.ondiemInitialized = '1';
    try {
      new ProAvailabilityWidget(container);
    } catch (error) {
      container.dataset.ondiemInitialized = '';
      console.error('Failed to initialize widget:', error);
    }
  });
}

